import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import ClientProposalTemplateSchema, {
  type ClientProposalTemplateJSON,
  type UpdateClientProposalTemplateRequest,
} from "work/client-proposal-templates/_schema/client-proposal-template-schema";

/**
 * The calling organization's firm onboarding defaults.
 *
 * **Neither endpoint takes an id**, and that is the contract rather than an omission. The record
 * sets the terms of proposals that go out in this organization's name, so the API resolves it from
 * the bearer token: there is nothing in a route to get wrong, and no way to read or write another
 * client's. The status board applies the same rule from the other direction, where the id IS sent
 * and the server refuses a set the caller may not have.
 */
export const clientProposalTemplateApi = api.injectEndpoints({
  endpoints: (build) => ({
    getClientProposalTemplate: build.query<ClientProposalTemplateJSON, void>({
      query: () => "work/client-proposal-templates",
      rawResponseSchema: ClientProposalTemplateSchema.ClientProposalTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: ["ClientProposalTemplate"],
      transformErrorResponse: (response: FetchBaseQueryError, meta) =>
        toClientProposalTemplateError(response, meta),
    }),

    /**
     * Copies a saved proposal template into the defaults, or changes only the switch.
     *
     * The copy is the server's own: the saved template's later edits, and its deletion, do not
     * reach it. A 403 means the caller may not configure the defaults or did not save that
     * template; a 404 means there is no such template.
     */
    updateClientProposalTemplate: build.mutation<
      ClientProposalTemplateJSON,
      UpdateClientProposalTemplateRequest
    >({
      query: (request) => ({
        url: "work/client-proposal-templates",
        method: "PUT",
        body: request,
      }),
      rawResponseSchema: ClientProposalTemplateSchema.ClientProposalTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      // The roll-out board reads whether a firm's draft was built, and this is what decides it.
      invalidatesTags: ["ClientProposalTemplate", "VendorRolloutBoard"],
      transformErrorResponse: (response: FetchBaseQueryError, meta) =>
        toClientProposalTemplateError(response, meta),
    }),
  }),
});

export const { useGetClientProposalTemplateQuery, useUpdateClientProposalTemplateMutation } =
  clientProposalTemplateApi;

/**
 * The caller may read their organization's proposals but may not price them, or may not reach this
 * configuration at all. Distinct from a general failure because the page says something different:
 * there is nothing to retry.
 */
export class UnauthorizedClientProposalTemplateError extends Error {
  public constructor() {
    super("You do not have permission to change your organization's proposal terms.");
  }
}

export class ClientProposalTemplateAPIError extends Error {
  public methodName?: string;
  public error: unknown;

  public constructor(methodName: string | undefined, error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(
      `Error in clientProposalTemplateApi${endpointName}: ${
        getErrorMessageFromUnknown(error) ?? "An error occurred"
      }`,
    );
    this.methodName = methodName;
    this.error = error;
  }
}

export function isUnauthorizedClientProposalTemplateError(error: unknown): boolean {
  return error instanceof UnauthorizedClientProposalTemplateError;
}

function toClientProposalTemplateError(response: FetchBaseQueryError, meta?: unknown): Error {
  if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) {
    return new UnauthorizedClientProposalTemplateError();
  }
  return new ClientProposalTemplateAPIError(getApiEndpointName(meta), response);
}
